import { ReactNode, RefObject, UIEventHandler, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useResizeObserver } from '@alltrails/core';
import SidebarTitleArea from './SidebarTitleArea';
import styles from './Sidebar.module.scss';
import { useSidebarContext } from './SidebarContext';

/**
 * A hook to complement the `Sidebar` component. This is split from the component to allow for flexibility in what consumers can render in either the header or content elements with the [parallel routing paradigm](https://nextjs.org/docs/app/building-your-application/routing/parallel-routes) provided by NextJS.
 *
 * Wrapping content by the returned functions will ensure the sidebar collapses and expands correctly. This assumes anything wrapped in `SidebarHeader` will still be partially visible when collapsed, and all other content will be hidden.
 *
 * For an example, see the `Sidebar.stories.tsx` story in the storybook package.
 *
 * @param collapseBehavior - Dictates if the sidebar can be collapsed, and if so, how it should behave.
 * @param fullHeight - If true, the sidebar will expand to full height
 * @param negativeContentMargin - A negative margin to apply to the content area. This is useful if the content area should be closer to the title element, without the smaller margin affecting the collapsed state.
 * @param onBackRequest - A callback function to be called when the back button is clicked. If present, the back button will be shown.
 * @param onContentScroll - A callback function to be called when the content area is scrolled.
 * @param preventHeightRecalculation - If true, the sidebar will not recalculate its height when the content changes.
 * @param additionalButton - An optional React element to render in the title area next to the collapse button.
 * @returns two functions to render header and content areas.
 */
export function useSidebar({
  collapseBehavior = 'none',
  fullHeight,
  negativeContentMargin,
  onBackRequest,
  onContentScroll,
  preventHeightRecalculation,
  additionalButton
}: {
  collapseBehavior?: 'full' | 'vertical-only' | 'none';
  fullHeight?: boolean;
  negativeContentMargin?: number;
  onBackRequest?: () => void;
  onContentScroll?: UIEventHandler<HTMLDivElement>;
  preventHeightRecalculation?: boolean;
  additionalButton?: React.ReactElement;
}) {
  const {
    collapsed,
    maxContentHeight,
    setCollapseBehavior,
    setContentHeight,
    setFooterHeight,
    setFullHeight,
    setPreventHeightRecalculation,
    setTitleHeight,
    toggleCollapse
  } = useSidebarContext();

  useEffect(() => {
    setCollapseBehavior(collapseBehavior);
  }, [collapseBehavior, setCollapseBehavior]);

  useEffect(() => {
    setPreventHeightRecalculation(!!preventHeightRecalculation);
  }, [preventHeightRecalculation, setPreventHeightRecalculation]);

  useEffect(() => {
    setFullHeight(Boolean(fullHeight));
  }, [fullHeight, setFullHeight]);

  const titleRef = useRef<HTMLDivElement>(null);
  const titleResizeHandler = useCallback(
    entries => {
      setTitleHeight((entries[0].target as HTMLDivElement).clientHeight);
    },
    [setTitleHeight]
  );
  const [observeTitle, disconnectTitle] = useResizeObserver(titleResizeHandler);
  useEffect(() => {
    observeTitle(titleRef.current);
    return disconnectTitle;
  }, [observeTitle, disconnectTitle]);

  const SidebarHeader = useCallback(
    (children: ReactNode, { hideOverflow, showScrollShadow }: { hideOverflow?: boolean; showScrollShadow?: boolean } = {}) => {
      return (
        <div className={classNames(styles.header, { [styles.scrollShadow]: showScrollShadow })}>
          <SidebarTitleArea
            ref={titleRef}
            canCollapse={collapseBehavior !== 'none'}
            collapsed={collapsed}
            hideOverflow={hideOverflow}
            onBackRequest={onBackRequest}
            onCollapseRequest={toggleCollapse}
            additionalButton={additionalButton}
          >
            {children}
          </SidebarTitleArea>
        </div>
      );
    },
    [collapseBehavior, collapsed, onBackRequest, toggleCollapse, additionalButton]
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const contentResizeHandler = useCallback(
    entries => {
      setContentHeight((entries[0].target as HTMLDivElement).clientHeight);
    },
    [setContentHeight]
  );
  const [observeContent, disconnectContent] = useResizeObserver(contentResizeHandler);
  useEffect(() => {
    observeContent(contentRef.current);
    return disconnectContent;
  }, [observeContent, disconnectContent]);

  const SidebarContent = useCallback(
    (children: ReactNode, { scrollRef }: { scrollRef?: RefObject<HTMLDivElement> } = {}) => {
      return (
        <div
          className={classNames(styles.scrollableContent, styles.collapsibleContent, {
            [styles.collapsed]: collapsed
          })}
          onScroll={onContentScroll}
          ref={scrollRef}
          style={{
            maxHeight: maxContentHeight ? maxContentHeight + (negativeContentMargin ?? 0) : maxContentHeight,
            marginTop: -(negativeContentMargin ?? 0)
          }}
        >
          <div ref={contentRef}>{children}</div>
        </div>
      );
    },
    [collapsed, maxContentHeight, contentRef, onContentScroll, negativeContentMargin]
  );

  const footerRef = useRef<HTMLDivElement>(null);
  const footerResizeHandler = useCallback(
    entries => {
      setFooterHeight((entries[0].target as HTMLDivElement).clientHeight);
    },
    [setFooterHeight]
  );
  const [observeFooter, disconnectFooter] = useResizeObserver(footerResizeHandler);
  useEffect(() => {
    observeFooter(footerRef.current);
    return disconnectFooter;
  }, [observeFooter, disconnectFooter]);

  const SidebarFooter = useCallback(
    (children: ReactNode, { showScrollShadow }: { showScrollShadow?: boolean } = {}) => {
      return !collapsed && !!children ? (
        <div
          className={classNames(styles.footer, styles.collapsibleContent, {
            [styles.collapsed]: collapsed,
            [styles.scrollShadow]: showScrollShadow
          })}
          ref={footerRef}
        >
          {children}
        </div>
      ) : (
        <div ref={footerRef} /> // placeholder element to ensure the ref can be observed
      );
    },
    [collapsed, footerRef]
  );

  return {
    SidebarHeader,
    SidebarContent,
    SidebarFooter
  };
}
